import { Fragment, useState } from "react";
import type { EndSeriesRequest, EndSeriesResponse, SeriesEndConfirmDetails } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { errorText } from "./common.ts";
import {
  BAD_CONFIRM_TEXT,
  BAD_RETRY_TEXT,
  PANEL_ASK_TEXT,
  PANEL_TITLE,
  REASON_NOT_SENT,
  cardAskText,
  confirmDetailsOf,
  confirmView,
  endFromLabel,
  endedText,
  lessonStartedOf,
  lessonStartedText,
  pastText,
  safeFromOf,
  unchangedText,
} from "./series-end-text.ts";

/**
 * Завершение серии пары с даты: форма в карточке занятия и панель на экране
 * серии. Разбор поведения в `docs/schedule-design.md`, раздел 5.6, слова в
 * `series-end-text.ts`.
 *
 * Шаги одни у обоих входов: что исчезнет; перечень потерь, если сервер его
 * прислал; повтор с датой сервера при отказе по дате; итог с «Готово».
 *
 * ⚠️ **Хук живёт ВНУТРИ формы и панели, а не выше.** Каждый вход в форму
 * начинается со свежего состояния: подтверждение, показанное вчера по чужому
 * списку, не должно встретить человека при следующем открытии карточки.
 */

type Exec = (fn: () => Promise<void>) => Promise<void>;
type Confirm = SeriesEndConfirmDetails;
type View =
  | { kind: "ask" }
  /** `details === null`: тело кривое. `previous`: перечень, подтверждённый перед этим. */
  | { kind: "confirm"; details: Confirm | null; previous: Confirm | null }
  /** `from === null`: тело отказа кривое, повторять не с чем. */
  | { kind: "retry"; text: string; from: string | null }
  | { kind: "notice"; text: string; response: EndSeriesResponse };

type EndSeries = ReturnType<typeof useEndSeries>;

/**
 * Состояние и запросы завершения. `exec` это исполнитель вызывающего: `run`
 * карточки (занятость, отказ строкой карточки, перечитывание недели) или
 * обёртка экрана серии, которая ошибку отдаёт сюда, в саму панель.
 *
 * ⚠️ **Правило завершения одно:** `onDone` зовётся сразу только при `ended` или
 * `deleted` без `heldBy`. Итог с `heldBy` (конец позже нажатого) и `unchanged`
 * человек обязан прочитать, поэтому они ждут «Готово»: у итога оно зовёт
 * `onDone`, у `unchanged` возвращает форму в начало и зовёт `onClose`.
 */
function useEndSeries(a: {
  seriesId: string;
  initialFrom: string | null;
  exec: Exec;
  onDone: (res: EndSeriesResponse) => void;
  onClose: () => void;
}) {
  const [view, setView] = useState<View>({ kind: "ask" });
  const [from, setFrom] = useState(a.initialFrom);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setView({ kind: "ask" });
    setFrom(a.initialFrom);
    setError(null);
  };

  const settle = (res: EndSeriesResponse) => {
    if (res.outcome !== "unchanged" && res.heldBy === null) {
      a.onDone(res);
      return;
    }
    const now = Date.now();
    const text = res.outcome === "unchanged" ? unchangedText(res.endsOn, now) : endedText(res, now);
    setView({ kind: "notice", text, response: res });
  };

  /** Отказы, у которых есть свой шаг. Остальные уходят исполнителю. */
  const refusal = (e: api.ApiError, shown: SeriesEndConfirmDetails | null): boolean => {
    if (e.code === "series_end_confirm") {
      setView({ kind: "confirm", details: confirmDetailsOf(e.details), previous: shown });
      return true;
    }
    if (e.code !== "series_end_past" && e.code !== "lesson_started") return false;
    // Повтор ЗАМЕНЯЕТ дату: с ней же потом уйдёт и подтверждение перечня
    const now = Date.now();
    const held = e.code === "lesson_started" ? lessonStartedOf(e.details) : null;
    const safe = e.code === "lesson_started" ? (held?.from ?? null) : safeFromOf(e.details);
    if (safe !== null) setFrom(safe);
    let text = BAD_RETRY_TEXT;
    if (safe !== null) text = held === null ? pastText(safe, now) : lessonStartedText(held, now);
    setView({ kind: "retry", text, from: safe });
    return true;
  };

  /**
   * Запрос. `shown` это перечень, который человек подтвердил: с ним уходят
   * `force`, id показанных уроков (`[]`, если их не было) и показанный вид
   * исхода, и сервер переспросит, если с тех пор что-то добавилось.
   */
  const send = (sendFrom: string | null, shown: SeriesEndConfirmDetails | null) => {
    const body: EndSeriesRequest = {
      ...(sendFrom === null ? {} : { from: sendFrom }),
      ...(shown === null
        ? {}
        : {
            force: true,
            expectLessonIds: shown.lessons.map((l) => l.id),
            expectDeletes: shown.deletes,
          }),
    };
    setError(null);
    void (async () => {
      try {
        await a.exec(async () => {
          try {
            settle(await api.endSeries(a.seriesId, body));
          } catch (e) {
            if (e instanceof api.ApiError && refusal(e, shown)) return;
            throw e;
          }
        });
      } catch (e) {
        // Сюда доходит только исполнитель экрана серии: `run` карточки ловит сам
        setError(errorText(e));
      }
    })();
  };

  return {
    view,
    from,
    error,
    /** Первый шаг и повтор: с той датой, что сейчас в состоянии (отказ её уже заменил). */
    start: () => send(from, null),
    /** Подтверждение показанного перечня с текущей датой. */
    force: (shown: SeriesEndConfirmDetails) => send(from, shown),
    back: () => setView({ kind: "ask" }),
    reset,
    finish: (res: EndSeriesResponse) => {
      reset();
      if (res.outcome === "unchanged") a.onClose();
      else a.onDone(res);
    },
  };
}

/**
 * Карточка занятия: «Убрать это и все следующие».
 *
 * ⚠️ Корень это `div.note`, прямой ребёнок `.wk__tools`: стили ставят таких
 * детей отдельной строкой под рядом кнопок, как вторую ступень отмены.
 */
export function EndSeriesForm(props: {
  seriesId: string;
  /** Дата места нажатого занятия в зоне серии. */
  from: string;
  /** В форме отмены набрана причина: сказать, что с завершением она не уйдёт. */
  reasonTyped: boolean;
  busy: boolean;
  run: Exec;
  /** «Не надо» на первом шаге: обратно в форму отмены, причина на месте. */
  onBack: () => void;
  /** Закрыть режим: после завершения или по «Готово». */
  onClose: () => void;
}) {
  const end = useEndSeries({
    seriesId: props.seriesId,
    initialFrom: props.from,
    exec: props.run,
    onDone: () => props.onClose(),
    onClose: props.onClose,
  });
  const now = Date.now();

  return (
    <div className="note">
      {end.view.kind === "ask" ? (
        <>
          <p>{cardAskText(end.from ?? props.from, now)}</p>
          {props.reasonTyped && <p>{REASON_NOT_SENT}</p>}
          <Foot
            busy={props.busy}
            onBack={props.onBack}
            go={{ label: "Завершить", onClick: end.start }}
          />
        </>
      ) : (
        <EndSeriesLater end={end} busy={props.busy} now={now} onBack={props.onBack} />
      )}
    </div>
  );
}

/**
 * Экран серии: панель «Если занятия закончились» под днями и временем.
 *
 * Запрос уходит без даты: сервер сам берёт самую раннюю, которая не задевает
 * начавшиеся занятия и проведённые уроки, поэтому тупика «нельзя» здесь нет.
 *
 * ⚠️ **Подтверждение красное, потому что ветка удаления необратима:** серия без
 * прошедших занятий удаляется целиком. Закрытие датой обратимо полем
 * «Окончание», но какая ветка выйдет, до ответа сервера неизвестно, и вес
 * обязан отвечать худшей.
 *
 * ⚠️ Панель держит свой итог, пока его не прочитали: при `available === false`
 * она пропадает, только если итога на ней нет. Иначе перечитывание серии сняло
 * бы панель вместе со строкой, которую человек ещё не прочёл.
 */
export function EndSeriesPanel(props: {
  seriesId: string;
  /** У серии нет конца или он позже сегодняшнего дня в зоне серии. */
  available: boolean;
  busy: boolean;
  exec: Exec;
  onDone: (res: EndSeriesResponse) => void;
}) {
  /** Первое нажатие только показывает красную кнопку, запрос уходит вторым. */
  const [armed, setArmed] = useState(false);
  const end = useEndSeries({
    seriesId: props.seriesId,
    initialFrom: null,
    exec: props.exec,
    onDone: props.onDone,
    onClose: () => setArmed(false),
  });
  if (!props.available && end.view.kind !== "notice") return null;
  const now = Date.now();
  const back = () => {
    end.reset();
    setArmed(false);
  };

  return (
    <section className="card">
      <header className="card__head">
        <h2>{PANEL_TITLE}</h2>
      </header>
      {end.view.kind === "ask" ? (
        <>
          <p className="muted">{PANEL_ASK_TEXT}</p>
          {end.error !== null && <p className="err">{end.error}</p>}
          {armed ? (
            <Foot busy={props.busy} onBack={back} go={{ label: "Завершить", onClick: end.start }} />
          ) : (
            <p>
              <button
                type="button"
                className="btn"
                disabled={props.busy}
                onClick={() => setArmed(true)}
              >
                Завершить серию
              </button>
            </p>
          )}
        </>
      ) : (
        <EndSeriesLater end={end} busy={props.busy} now={now} onBack={back} />
      )}
    </section>
  );
}

/**
 * Всё после первого шага: перечень потерь, повтор с датой сервера и итог.
 * Общее у карточки и экрана серии, чтобы слова и порядок кнопок не разъехались.
 */
function EndSeriesLater({
  end,
  busy,
  now,
  onBack,
}: {
  end: EndSeries;
  busy: boolean;
  now: number;
  /** «Не надо» у повтора: туда же, куда «Не надо» первого шага. */
  onBack: () => void;
}) {
  const { view } = end;
  const err = end.error !== null && <p className="err">{end.error}</p>;

  if (view.kind === "notice") {
    return (
      <>
        <p>{view.text}</p>
        <div className="form__foot">
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => end.finish(view.response)}
          >
            Готово
          </button>
        </div>
      </>
    );
  }

  if (view.kind === "retry") {
    const retryFrom = view.from;
    return (
      <>
        <p>{view.text}</p>
        {err}
        <Foot
          busy={busy}
          onBack={onBack}
          go={
            retryFrom === null
              ? null
              : { label: endFromLabel(retryFrom, now), onClick: end.start }
          }
        />
      </>
    );
  }

  if (view.kind !== "confirm") return null;
  const details = view.details;
  const shown = details === null ? null : confirmView(details, view.previous, now);

  return (
    <>
      {shown === null ? (
        <p>{BAD_CONFIRM_TEXT}</p>
      ) : (
        <>
          {shown.lead.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {shown.points.map((point) => (
            <Fragment key={point.text}>
              <p>{point.text}</p>
              {point.rows.length > 0 && (
                <ul>
                  {point.rows.map((row) => (
                    <li key={row.id}>{row.text}</li>
                  ))}
                </ul>
              )}
            </Fragment>
          ))}
          {shown.outcome !== null && <p>{shown.outcome}</p>}
        </>
      )}
      {err}
      <Foot
        busy={busy}
        onBack={end.back}
        go={
          details === null
            ? null
            : { label: "Всё равно завершить", onClick: () => end.force(details) }
        }
      />
    </>
  );
}

/**
 * Подвал шага: тихое «Не надо» и, если шагу есть куда идти, красная кнопка.
 *
 * Заливка красным у всех шагов, а не только у последнего: любое из этих
 * нажатий может убрать занятия сразу, если сервер не нашёл, что спросить.
 * Акцента рядом нет, экран спрашивает про необратимое.
 */
function Foot(props: {
  busy: boolean;
  onBack: () => void;
  /** `null`: идти некуда (тело отказа кривое), остаётся одно «Не надо». */
  go: { label: string; onClick: () => void } | null;
}) {
  return (
    <div className="form__foot">
      <button type="button" className="btn btn--quiet" disabled={props.busy} onClick={props.onBack}>
        Не надо
      </button>
      {props.go !== null && (
        <button
          type="button"
          className="btn btn--danger is-confirm"
          disabled={props.busy}
          onClick={props.go.onClick}
        >
          {props.go.label}
        </button>
      )}
    </div>
  );
}
